import React, { ComponentType } from 'react'
import styled from '@emotion/styled'
import { space, SpaceProps } from 'styled-system'
import Icon from 'shared/library/atoms/Icon'
import useTranslation from 'next-translate/useTranslation'
import Price, { StyledPrice } from 'shared/library/atoms/Price'
import { definedNotNull, RecursivePartial } from 'shared/presenters/presenter'
import {
  Credit_Vouchers,
  Discount_Codes,
  Maybe
} from 'shared/presenters/graphqlTypes'
import Button from 'library/atoms/Button'

export interface VoucherBarProps extends SpaceProps {
  /**
   * Takes a string to output a class. Related to emotion - It lets another component style it
   */
  className?: string
  onClick: () => void
  discount?: {
    text?: string
    data?: RecursivePartial<Maybe<Discount_Codes>>
  }
  credit?: {
    text?: string
    data?: RecursivePartial<Maybe<Credit_Vouchers>>
  }
  activity?: {
    isActive?: boolean
    text?: string
  }
}

type VoucherVariations = Extract<
  keyof VoucherBarProps,
  'credit' | 'discount' | 'activity'
>

/**
 *  The VoucherBar component is used to display voucher or discount code being
 *  active and info regarding type and remaining credit or percentage
 *  reduction.
 */
const VoucherBar = (props: VoucherBarProps) => {
  const { onClick, discount, credit, activity, className, ...other } = props
  const { t } = useTranslation('common')

  const determineVoucherVariant = ({
    activity,
    credit,
    discount
  }: Pick<VoucherBarProps, 'activity' | 'credit' | 'discount'>):
    | VoucherVariations
    | undefined => {
    if (activity?.isActive) {
      return 'activity'
    } else if (credit?.data && !activity?.isActive) {
      return 'credit'
    } else if (discount?.data && !activity?.isActive && !credit?.data) {
      return 'discount'
    }
  }

  const voucherContentToBeRendered = determineVoucherVariant({
    activity,
    credit,
    discount
  })

  if (!voucherContentToBeRendered) return null

  const VoucherContent = VouchersContent[voucherContentToBeRendered]

  return (
    <StyledVoucherBar
      onClick={onClick}
      {...other}
      aria-label={t('voucher-bar')}
      className={className}
    >
      <Icon name="voucher" size={32} color="gold" />
      <VoucherContent {...props} />
    </StyledVoucherBar>
  )
}

export default VoucherBar

const ActivityVoucherContent = () => {
  const { t } = useTranslation('common')
  return (
    <>
      <StyledText>{t('voucher-applied-at-checkout')}</StyledText>
      <StyledText>{t('gift')}</StyledText>
    </>
  )
}

const CreditVoucherContent = ({ credit }: Pick<VoucherBarProps, 'credit'>) => {
  const { t } = useTranslation('common')
  return (
    <>
      <StyledText>
        {credit?.text ? credit?.text : t('voucher-applied-at-checkout')}
      </StyledText>
      <Price
        pence={definedNotNull(credit?.data?.remaining_pence)}
        currency={definedNotNull(credit?.data?.amount_currency)}
        aria-label={t('amount-of-credit')}
      />
    </>
  )
}

const DiscountCodeContent = ({
  discount
}: Pick<VoucherBarProps, 'discount'>) => {
  const { t } = useTranslation('common')
  return (
    <>
      <StyledText>
        {discount?.text
          ? discount?.text
          : t('discount-code-applied-at-checkout')}
      </StyledText>
      <StyledText aria-label={t('percentage-discount')}>
        {t('x-percent', { count: discount?.data?.percent })}
      </StyledText>
    </>
  )
}

const VouchersContent: {
  [keys in VoucherVariations]: ComponentType<VoucherBarProps>
} = {
  activity: ActivityVoucherContent,
  credit: CreditVoucherContent,
  discount: DiscountCodeContent
}

const StyledVoucherBar = styled(Button)(
  ({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    background: theme.colors.primary,
    paddingTop: theme.fixedSpace[2],
    paddingBottom: theme.fixedSpace[2],
    marginTop: theme.space[1],
    border: 'none',
    color: theme.colors.white,
    ...theme.mixins.centeredContainer(theme.currentMaxWidth),
    [`${StyledPrice}`]: {
      right: theme.space[4],
      marginLeft: 'auto'
    },
    // When the button/banner is touching the screen edges
    [`@media screen and (max-width: ${theme.contentMaxWidth[theme.currentMaxWidth]}px)`]:
      {
        borderRadius: 0,
        marginTop: 0
      }
  }),
  space
)

/**
 * Using StyledText to avoid duplication of fontStyle prop each time we use Text component
 */
const StyledText = styled.span(({ theme }) => ({
  ...theme.fontStyle.h6,
  textAlign: 'right',
  marginLeft: theme.space[5],
  '&:first-of-type': {
    textAlign: 'left',
    flexGrow: 1,
    paddingRight: theme.space[2]
  },
  '&:last-of-type': {
    right: theme.space[4]
  }
}))
